package com.sweetlime.bingeamigo.presentation.features.tvdetail

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.sweetlime.bingeamigo.model.Episode
import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.model.toLibraryTvShow
import com.sweetlime.bingeamigo.model.types.ContentType
import com.sweetlime.bingeamigo.presentation.components.FriendReviewCard
import com.sweetlime.bingeamigo.presentation.components.RateReviewSheet
import com.sweetlime.bingeamigo.presentation.components.StarRatingBar
import com.sweetlime.bingeamigo.presentation.features.library.TvLibraryViewModel
import com.sweetlime.bingeamigo.presentation.features.review.FriendReviewsViewModel
import com.sweetlime.bingeamigo.presentation.features.review.ReviewViewModel
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.util.format1f
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun EpisodeDetailContent(
    tvShow: TvShow,
    episode: Episode,
    totalEpisodesInShow: Int,
    onBackClick: () -> Unit,
    tvLibraryViewModel: TvLibraryViewModel = koinViewModel(),
    reviewViewModel: ReviewViewModel = koinViewModel(),
    friendReviewsViewModel: FriendReviewsViewModel = koinViewModel()
) {
    val watchedEpisodes by tvLibraryViewModel.watchedEpisodesForShow(tvShow.id.toString())
        .collectAsStateWithLifecycle(emptyList())
    val reviewUiState by reviewViewModel.uiState.collectAsStateWithLifecycle()
    val friendReviewsState by friendReviewsViewModel.state.collectAsStateWithLifecycle()

    val libraryEpisode = watchedEpisodes.find { it.id == episode.id }
    val isWatched = libraryEpisode?.watched == true
    val contentId = "${tvShow.id}:${episode.id}"

    @OptIn(ExperimentalTime::class)
    val todayStr = remember {
        val ms = Clock.System.now().toEpochMilliseconds()
        val days = (ms / 86_400_000L).toInt()
        var n = days + 719468
        val era = if (n >= 0) n else n - 146096
        val doe = era % 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val y = yoe + (era / 146097) * 400
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = if (mp < 10) mp + 3 else mp - 9
        val year = if (m <= 2) y + 1 else y
        "${year}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }
    val isFuture = remember(episode.airDate) { episode.airDate.length == 10 && episode.airDate > todayStr }
    val airDateLabel = remember(episode.airDate) {
        if (episode.airDate.length == 10) {
            val months = listOf("Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec")
            val m = episode.airDate.substring(5, 7).toIntOrNull() ?: 0
            val d = episode.airDate.substring(8, 10).toIntOrNull() ?: 0
            "Airs ${months.getOrNull(m - 1) ?: ""} $d"
        } else ""
    }

    var showRateSheet by remember { mutableStateOf(false) }
    var watchedLoading by remember { mutableStateOf(false) }

    LaunchedEffect(contentId) {
        friendReviewsViewModel.load(ContentType.EPISODE, contentId)
    }
    LaunchedEffect(isWatched) { watchedLoading = false }
    LaunchedEffect(reviewUiState.saved) {
        if (reviewUiState.saved) {
            showRateSheet = false
            reviewViewModel.reset()
        }
    }

    if (showRateSheet) {
        RateReviewSheet(
            title = episode.name,
            initialRating = reviewUiState.existingReview?.rating ?: libraryEpisode?.userRating ?: 0.0,
            initialReview = reviewUiState.existingReview?.review ?: libraryEpisode?.userReview,
            isLoading = reviewUiState.isLoading,
            isSaving = reviewUiState.isSaving,
            onDismiss = {
                showRateSheet = false
                reviewViewModel.reset()
            },
            onSubmit = { rating, text ->
                val libEpisode = libraryEpisode ?: LibraryEpisode(
                    id = episode.id,
                    seasonNumber = episode.seasonNumber,
                    episodeNumber = episode.episodeNumber
                )
                reviewViewModel.submitEpisodeReview(
                    libraryTvShow = tvShow.toLibraryTvShow(),
                    episode = libEpisode,
                    episodeTitle = episode.name,
                    episodePosterPath = episode.stillPath.orEmpty(),
                    rating = rating,
                    reviewText = text
                )
            }
        )
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {
        LazyColumn(modifier = Modifier.fillMaxSize()) {

            // Hero still image with gradient overlay
            item {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .aspectRatio(16f / 9f)
                ) {
                    if (episode.stillPath != null) {
                        KamelImage(
                            resource = asyncPainterResource("https://image.tmdb.org/t/p/w780${episode.stillPath}"),
                            contentDescription = episode.name,
                            contentScale = ContentScale.Crop,
                            modifier = Modifier.fillMaxSize()
                        )
                    } else {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(Color(0xFF1A1A1A)),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = "S${episode.seasonNumber} · E${episode.episodeNumber}",
                                color = Color(0xFF555555),
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                    Box(
                        modifier = Modifier
                            .matchParentSize()
                            .background(
                                Brush.verticalGradient(
                                    colorStops = arrayOf(
                                        0.4f to Color.Transparent,
                                        1.0f to Color(0xFF121212)
                                    )
                                )
                            )
                    )
                }
            }

            // Episode metadata
            item {
                Column(
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 12.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "S${episode.seasonNumber}",
                            color = BrandColors.Secondary,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Text("·", color = Color.Gray, style = MaterialTheme.typography.labelMedium)
                        Text(
                            text = "E${episode.episodeNumber}",
                            color = BrandColors.Secondary,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold
                        )
                        if (episode.runtime != null && episode.runtime > 0) {
                            Text("·", color = Color.Gray, style = MaterialTheme.typography.labelMedium)
                            Text(
                                text = "${episode.runtime}m",
                                color = Color.Gray,
                                style = MaterialTheme.typography.labelMedium
                            )
                        }
                        if (episode.voteAverage > 0.0) {
                            Text("·", color = Color.Gray, style = MaterialTheme.typography.labelMedium)
                            Icon(
                                Icons.Filled.Star,
                                contentDescription = null,
                                tint = BrandColors.TextSecondary,
                                modifier = Modifier.size(12.dp)
                            )
                            Text(
                                text = (episode.voteAverage / 2).format1f(),
                                color = BrandColors.TextSecondary,
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }

                    Text(
                        text = episode.name,
                        color = Color.White,
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold
                    )

                    if (episode.airDate.isNotBlank()) {
                        Text(
                            text = episode.airDate,
                            color = Color(0xFF666666),
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            }

            // Action buttons
            item {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 20.dp, vertical = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Watched toggle
                    Button(
                        onClick = {
                            if (!isFuture) {
                                watchedLoading = true
                                if (isWatched) {
                                    tvLibraryViewModel.markEpisodeUnwatched(tvShow, episode.id)
                                } else {
                                    tvLibraryViewModel.markEpisodeWatched(
                                        tvShow,
                                        LibraryEpisode(
                                            id = episode.id,
                                            watched = true,
                                            seasonNumber = episode.seasonNumber,
                                            episodeNumber = episode.episodeNumber
                                        ),
                                        totalEpisodesInShow,
                                        episodeName = episode.name,
                                        episodeStillPath = episode.stillPath.orEmpty()
                                    )
                                }
                            }
                        },
                        enabled = !isFuture,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = when {
                                isFuture -> Color(0xFF2A2A2A)
                                isWatched -> BrandColors.Primary
                                else -> Color(0xFF2A2A2A)
                            },
                            disabledContainerColor = Color(0xFF2A2A2A)
                        ),
                        modifier = Modifier.weight(1f)
                    ) {
                        if (watchedLoading) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(18.dp),
                                strokeWidth = 2.dp,
                                color = if (isWatched) Color.Black else BrandColors.Primary
                            )
                        } else if (isFuture) {
                            Text(
                                text = airDateLabel,
                                color = Color.White.copy(alpha = 0.4f),
                                modifier = Modifier.padding(start = 6.dp)
                            )
                        } else {
                            Icon(
                                imageVector = if (isWatched) Icons.Filled.CheckCircle else Icons.Outlined.CheckCircle,
                                contentDescription = null,
                                tint = if (isWatched) Color.Black else Color.White,
                                modifier = Modifier.size(18.dp)
                            )
                            Text(
                                text = if (isWatched) "Watched" else "Mark watched",
                                color = if (isWatched) Color.Black else Color.White,
                                modifier = Modifier.padding(start = 6.dp)
                            )
                        }
                    }

                    // Rate button
                    val userRating = reviewUiState.existingReview?.rating
                        ?: libraryEpisode?.userRating
                        ?: 0.0
                    Button(
                        onClick = {
                            if (!isFuture) {
                                reviewViewModel.load(ContentType.EPISODE, contentId)
                                showRateSheet = true
                            }
                        },
                        enabled = !isFuture,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = if (userRating > 0.0) Color(0xFF2A2200) else Color(0xFF2A2A2A),
                            disabledContainerColor = Color(0xFF2A2A2A)
                        ),
                        modifier = Modifier.weight(1f)
                    ) {
                        Icon(
                            Icons.Filled.Star,
                            contentDescription = null,
                            tint = when {
                                isFuture -> Color.Gray.copy(alpha = 0.3f)
                                userRating > 0.0 -> BrandColors.Primary
                                else -> Color.Gray
                            },
                            modifier = Modifier.size(18.dp)
                        )
                        Text(
                            text = if (userRating > 0.0) userRating.format1f() else "Rate",
                            color = when {
                                isFuture -> Color.White.copy(alpha = 0.3f)
                                userRating > 0.0 -> BrandColors.Primary
                                else -> Color.White
                            },
                            modifier = Modifier.padding(start = 6.dp)
                        )
                    }
                }
            }

            // Overview
            if (episode.overview.isNotBlank()) {
                item {
                    HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                        color = Color.White.copy(alpha = 0.08f)
                    )
                    Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)) {
                        Text(
                            text = "Overview",
                            color = Color.White,
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = episode.overview,
                            color = Color.LightGray,
                            style = MaterialTheme.typography.bodyMedium,
                            lineHeight = 22.sp
                        )
                    }
                }
            }

            // Your review
            val displayedReview = reviewUiState.existingReview?.review ?: libraryEpisode?.userReview
            if (!displayedReview.isNullOrBlank()) {
                item {
                    HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                        color = Color.White.copy(alpha = 0.08f)
                    )
                    Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)) {
                        Text(
                            text = "Your review",
                            color = Color.Gray,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            text = displayedReview,
                            color = Color.LightGray,
                            style = MaterialTheme.typography.bodyMedium,
                            lineHeight = 20.sp
                        )
                    }
                }
            }

            // Friends' reviews
            val friendReviews = friendReviewsState.reviews
            if (friendReviews.isNotEmpty()) {
                item {
                    HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                        color = Color.White.copy(alpha = 0.08f)
                    )
                    Text(
                        text = "Friends' Reviews",
                        color = Color.White,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(start = 20.dp, end = 20.dp, bottom = 10.dp)
                    )
                }
                items(friendReviews) { review ->
                    FriendReviewCard(
                        review = review,
                        isLiked = review.docId in friendReviewsState.likedReviewIds,
                        onLikeClick = { friendReviewsViewModel.toggleLike(review) },
                        modifier = Modifier.padding(horizontal = 20.dp, vertical = 4.dp)
                    )
                }
            }

            item { Spacer(modifier = Modifier.height(120.dp)) }
        }

        // Top back button
        Box(modifier = Modifier.statusBarsPadding().padding(4.dp)) {
            IconButton(
                onClick = onBackClick,
                modifier = Modifier.background(Color.Black.copy(alpha = 0.5f), CircleShape)
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = "Back",
                    tint = Color.White
                )
            }
        }
    }
}
